import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scale } from "lucide-react";
import { useT } from "@/i18n";
import BrandMark from "@/components/BrandMark";

export default function Login() {
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("v.ramanauskas@rplaw.lt");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white">
        <div className="flex items-center gap-3">
          <BrandMark className="h-10 w-10" iconClassName="h-8 w-8 text-amber-500" />
          <span className="font-serif text-2xl font-semibold">RPLAW</span>
        </div>
        <div>
          <h1 className="font-serif text-4xl leading-snug tracking-tight">
            {t("auth.tagline1")}
            <br />
            <span className="text-amber-500">{t("auth.tagline2")}</span>
          </h1>
          <p className="mt-6 text-slate-400 text-sm max-w-md leading-relaxed">
            {t("auth.tagline_body")}
          </p>
        </div>
        <div className="text-xs text-slate-500">{t("auth.private")}</div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 px-6">
        <form
          onSubmit={submit}
          className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm"
          data-testid="login-form"
        >
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <BrandMark className="h-7 w-7" iconClassName="h-6 w-6 text-amber-600" />
            <span className="font-serif text-xl font-semibold text-slate-900">RPLAW</span>
          </div>
          <h2 className="font-serif text-2xl text-slate-900">{t("auth.sign_in")}</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">{t("auth.subtitle")}</p>

          {error && (
            <div
              data-testid="login-error"
              className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full mt-6 bg-slate-900 hover:bg-slate-800"
          >
            {loading ? t("auth.signing_in") : t("auth.sign_in")}
          </Button>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
            >
              {t("auth.forgot")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
